/** Artefacts step — review each LOA or cheat sheet produced by the run. */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare, faFileArrowDown } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

import { CompanyProvider } from "@/context/CompanyContext";
import { PDFConfigProvider } from "@/context/PDFConfigContext";
import { useIsOsMode, useOsNavigate } from "@/components/ui/os-layout";
import { agentReviewAPI } from "@/services/api/agentReview";
import { openDocumentTextArtifactPreview } from "@/services/api/agentic-v2";
import { openAdviceDeckPdfPreview } from "./adviceDeckPreview";
import { artefactCriteriaForItem, runHasReviewableArtefacts } from "./artefactCriteria";
import { RatingPanel } from "./RatingPanel";
import { PhaseInstruction } from "./PhaseInstruction";
import { ArtefactPreview } from "./ArtefactPreview";
import {
  isDocumentTextReviewArtefact,
  isCheatSheetReviewArtefact,
  isReportDeckReviewArtefact,
} from "./artefactKind";
import { artefactReviewEmptyMessage, artefactReviewInstruction } from "./artefactReviewCopy";
import { AceArtefactComparison } from "./AceArtefactComparison";
import { ArtefactApprovalSelector } from "./ArtefactApprovalSelector";
import { artefactPlanLabel, portfolioPath } from "./artefactPortfolioLink";
import {
  applyCheatSheetVariantAutoNa,
  cheatSheetAutoNaCheckIds,
  cheatSheetVariantFromArtefact,
  cheatSheetVariantLabel,
} from "./cheatSheetVariantChecks";
import { failCommentsComplete } from "./checkComments";
import type {
  AgentRun,
  ArtefactApprovalDecision,
  ArtefactRatings,
  CheckAnswer,
  PhaseCriteria,
  Rag,
  ReviewComparison,
} from "./types";
import { deriveRag } from "./deriveRag";

interface ArtefactsPhaseProps {
  run: AgentRun;
  ratings: ArtefactRatings;
  onChange: (
    artifactId: string,
    rating:
      | ArtefactRatings[string]
      | ((prev: ArtefactRatings[string] | undefined) => ArtefactRatings[string]),
  ) => void;
  aceComparison?: ReviewComparison | null;
  showAceComparison?: boolean;
  aceLoading?: boolean;
  activeArtifactId?: string | null;
  focusCheckId?: string | null;
}

export function ArtefactsPhase({
  run,
  ratings,
  onChange,
  aceComparison = null,
  showAceComparison = false,
  aceLoading = false,
  activeArtifactId = null,
  focusCheckId: focusCheckIdProp = null,
}: ArtefactsPhaseProps) {
  const navigate = useNavigate();
  const osNavigate = useOsNavigate();
  const isOsMode = useIsOsMode();
  const artefacts = run.artefacts ?? [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [localFocusCheckId, setLocalFocusCheckId] = useState<string | null>(null);
  const [exportingFunds, setExportingFunds] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [openingPdfPreview, setOpeningPdfPreview] = useState(false);
  const activeArtefact = artefacts[Math.min(activeIndex, Math.max(artefacts.length - 1, 0))];
  const activeArtefactIdResolved = activeArtefact?.artifact_id ?? null;
  const activeCriteria = activeArtefact ? artefactCriteriaForItem(run, activeArtefact) : null;

  useEffect(() => {
    if (!activeArtifactId) return;
    const index = artefacts.findIndex((item) => item.artifact_id === activeArtifactId);
    if (index >= 0) setActiveIndex(index);
  }, [activeArtifactId, artefacts]);

  useEffect(() => {
    setLocalFocusCheckId(null);
    setExportError(null);
  }, [activeIndex]);

  useEffect(() => {
    if (!activeCriteria || !activeArtefact || !isCheatSheetReviewArtefact(activeArtefact, run.catalogue_key)) {
      return;
    }
    if (!activeArtefactIdResolved) return;

    const artefact = activeArtefact;
    onChange(activeArtefactIdResolved, (prev) => {
      const current = prev ?? emptyRating();
      return applyCheatSheetVariantAutoNa(artefact, activeCriteria, current, run.catalogue_key);
    });
  }, [
    activeArtefactIdResolved,
    activeArtefact?.cheat_sheet_variant,
    activeArtefact?.cheat_sheet_mode,
    activeArtefact?.cheat_sheet_uses_proxy,
    activeArtefact?.artifact_kind,
    activeCriteria,
    onChange,
    run.catalogue_key,
  ]);

  if (!runHasReviewableArtefacts(run) || artefacts.length === 0) {
    return (
      <p className="text-sm text-slate-400">{artefactReviewEmptyMessage(artefacts, run.catalogue_key)}</p>
    );
  }

  const artefact = artefacts[Math.min(activeIndex, artefacts.length - 1)];
  const criteria = artefactCriteriaForItem(run, artefact);
  if (!criteria) {
    return (
      <p className="text-sm text-slate-400">{artefactReviewEmptyMessage(artefacts, run.catalogue_key)}</p>
    );
  }
  const artefactId = artefact.artifact_id;
  const rating = ratings[artefactId] ?? emptyRating();

  const updateRating = (
    patch: (current: ArtefactRatings[string]) => ArtefactRatings[string],
  ) => {
    onChange(artefactId, (prev) => patch(prev ?? emptyRating()));
  };

  const setAnswer = (checkId: string, answer: CheckAnswer) => {
    updateRating((current) => {
      const answers = { ...current.answers, [checkId]: answer };
      const check_comments =
        answer !== "fail"
          ? { ...current.check_comments, [checkId]: "" }
          : current.check_comments;
      const derived = deriveRag(criteria, answers);
      const wasFollowing =
        current.human_rag === null || current.human_rag === current.suggested_rag;
      return {
        ...current,
        answers,
        check_comments,
        suggested_rag: derived.rag,
        human_rag: wasFollowing ? derived.rag : current.human_rag,
      };
    });
  };

  const setCheckComment = (checkId: string, comment: string) => {
    updateRating((current) => ({
      ...current,
      check_comments: { ...current.check_comments, [checkId]: comment },
    }));
  };

  const setOverride = (rag: Rag) => {
    updateRating((current) => ({ ...current, human_rag: rag }));
  };

  const setNote = (note: string) => {
    updateRating((current) => ({ ...current, human_note: note }));
  };

  const setApproval = (human_approval: ArtefactApprovalDecision) => {
    updateRating((current) => ({ ...current, human_approval }));
  };

  const effectiveFocusCheckId = localFocusCheckId ?? focusCheckIdProp;
  const checklistComplete = artefactChecklistComplete(criteria, rating);
  const portfolioId = artefact.portfolio_id?.trim() || null;
  const isCheatSheet = isCheatSheetReviewArtefact(artefact, run.catalogue_key);
  const isReportDeck = isReportDeckReviewArtefact(artefact);
  const isDocumentText = isDocumentTextReviewArtefact(artefact);
  const showPortfolioTools = isCheatSheet && Boolean(portfolioId);
  const cheatSheetVariant = cheatSheetVariantFromArtefact(artefact, run.catalogue_key);
  const autoNaCheckIds = cheatSheetVariant
    ? cheatSheetAutoNaCheckIds(cheatSheetVariant)
    : undefined;
  const planLabel = artefactPlanLabel(artefact);

  const openPortfolio = () => {
    if (!portfolioId) return;
    const path = portfolioPath(portfolioId);
    if (isOsMode) {
      osNavigate(path, planLabel);
      return;
    }
    navigate(path);
  };

  const exportPortfolioFunds = async () => {
    if (!portfolioId || exportingFunds) return;
    setExportingFunds(true);
    setExportError(null);
    try {
      await agentReviewAPI.downloadPortfolioFundDetails(portfolioId);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Portfolio fund export failed");
    } finally {
      setExportingFunds(false);
    }
  };

  const openFullPreview = async () => {
    if (openingPdfPreview) return;
    setOpeningPdfPreview(true);
    setExportError(null);
    try {
      if (isReportDeck) {
        await openAdviceDeckPdfPreview(artefact.artifact_id, artefact);
      } else if (isDocumentText) {
        await openDocumentTextArtifactPreview(artefact.artifact_id, artefact.title);
      }
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Couldn't open preview");
    } finally {
      setOpeningPdfPreview(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-hidden">
      <PhaseInstruction>{artefactReviewInstruction(artefacts, run.catalogue_key)}</PhaseInstruction>

      {artefacts.length > 1 ? (
        <div className="flex flex-shrink-0 flex-wrap gap-2">
          {artefacts.map((item, index) => {
            const done = Boolean(ratings[item.artifact_id]?.human_rag);
            const active = index === activeIndex;
            return (
              <button
                key={item.artifact_id}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium ring-1 transition-colors ${
                  active
                    ? "bg-indigo-500/20 ring-indigo-400/40 text-indigo-100"
                    : "bg-white/[0.03] ring-white/10 text-slate-300 hover:bg-white/[0.06]"
                }`}
              >
                {done && !active ? "✓ " : ""}
                {item.title}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="grid min-h-0 flex-1 gap-6 overflow-y-auto lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:grid-rows-[minmax(0,1fr)] lg:items-stretch lg:overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          <div className="mb-3 flex flex-shrink-0 items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="truncate text-sm font-medium text-slate-100">{artefact.title}</h3>
              {artefact.deck_mode ? (
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  Deck mode: {artefact.deck_mode.replace(/_/g, " ")}
                </p>
              ) : artefact.plan_id || artefact.plan_name ? (
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  {artefact.plan_name || `Plan ${artefact.plan_id}`}
                </p>
              ) : null}
            </div>
            <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-3">
              {showPortfolioTools ? (
                <button
                  type="button"
                  onClick={() => void exportPortfolioFunds()}
                  disabled={exportingFunds}
                  className="inline-flex items-center gap-1.5 text-xs text-indigo-300 hover:text-indigo-200 disabled:opacity-50"
                  data-testid="artefact-export-portfolio-funds"
                >
                  <FontAwesomeIcon icon={faFileArrowDown} className="text-[10px]" />
                  {exportingFunds ? "Exporting…" : "Export fund details"}
                </button>
              ) : null}
              {showPortfolioTools ? (
                <button
                  type="button"
                  onClick={openPortfolio}
                  className="inline-flex items-center gap-1.5 text-xs text-indigo-300 hover:text-indigo-200"
                  data-testid="artefact-open-portfolio"
                >
                  Open portfolio
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
                </button>
              ) : null}
              {isReportDeck || isDocumentText || artefact.preview_path ? (
                isReportDeck || isDocumentText ? (
                  <button
                    type="button"
                    onClick={() => void openFullPreview()}
                    disabled={openingPdfPreview}
                    className="inline-flex items-center gap-1.5 text-xs text-indigo-300 hover:text-indigo-200 disabled:opacity-50"
                  >
                    {openingPdfPreview ? "Opening…" : "Full preview"}
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
                  </button>
                ) : (
                  <a
                    href={artefact.preview_path!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-indigo-300 hover:text-indigo-200"
                  >
                    Full preview
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
                  </a>
                )
              ) : null}
            </div>
          </div>

          {isCheatSheet && !showPortfolioTools ? (
            <p className="mb-2 text-xs text-slate-500" data-testid="artefact-no-portfolio-hint">
              {artefact.cheat_sheet_mode === "data_only"
                ? "No portfolio for this cheat sheet — it was rendered data-only (e.g. DB pension or final salary with no unit holdings)."
                : "No portfolio linked to this cheat sheet."}
            </p>
          ) : null}

          {exportError ? (
            <p className="mb-2 text-xs text-rose-400" data-testid="artefact-export-error">
              {exportError}
            </p>
          ) : null}

          <div className="flex min-h-0 flex-1 flex-col">
            <CompanyProvider companyId="">
              <PDFConfigProvider configId="">
                <ArtefactPreview
                  artefact={artefact}
                  catalogueKey={run.catalogue_key}
                  className="min-h-0 flex-1"
                />
              </PDFConfigProvider>
            </CompanyProvider>
          </div>
        </div>

        <div className="relative z-10 flex min-h-0 min-w-0 flex-col gap-5 lg:overflow-y-auto lg:overscroll-contain">
          <div className="mb-0 flex flex-shrink-0 items-center justify-between gap-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Your rating
            </p>
            {cheatSheetVariant ? (
              <span
                className="rounded-md bg-white/[0.04] px-2 py-0.5 text-[10px] font-medium text-slate-400 ring-1 ring-white/10"
                data-testid="cheat-sheet-variant-badge"
              >
                {cheatSheetVariantLabel(cheatSheetVariant)}
              </span>
            ) : null}
          </div>
          <RatingPanel
            criteria={criteria}
            answers={rating.answers}
            checkComments={rating.check_comments ?? {}}
            rag={rating.human_rag}
            note={rating.human_note}
            onAnswer={setAnswer}
            onCheckComment={setCheckComment}
            onOverride={setOverride}
            onNote={setNote}
            compact
            autoNaCheckIds={autoNaCheckIds}
            autoNaReason={
              cheatSheetVariant ? cheatSheetVariantLabel(cheatSheetVariant) : undefined
            }
          />
          <ArtefactApprovalSelector
            value={rating.human_approval}
            onChange={setApproval}
            compact
          />
          {showAceComparison && checklistComplete ? (
            <AceArtefactComparison
              run={run}
              artefact={artefact}
              criteria={criteria}
              humanAnswers={rating.answers}
              humanCheckComments={rating.check_comments ?? {}}
              humanRag={rating.human_rag}
              comparison={aceComparison}
              loading={aceLoading}
              focusCheckId={effectiveFocusCheckId}
              onFocusCheck={setLocalFocusCheckId}
            />
          ) : showAceComparison ? (
            <div
              className="rounded-xl bg-white/[0.02] px-4 py-3 ring-1 ring-white/5"
              data-testid="ace-checklist-gate"
            >
              <p className="text-xs text-slate-500">
                Answer every checklist item above before Ace reviews this document.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function emptyRating() {
  return {
    answers: {},
    check_comments: {},
    suggested_rag: null,
    human_rag: null,
    human_note: "",
    human_approval: null,
  };
}

export function artefactChecklistComplete(
  criteria: PhaseCriteria,
  rating: ArtefactRatings[string] | undefined,
): boolean {
  if (!rating) return false;
  for (const check of criteria.checks) {
    if (!rating.answers[check.id]) return false;
  }
  return failCommentsComplete(criteria, rating.answers, rating.check_comments);
}

export function artefactRatingsComplete(run: AgentRun, ratings: ArtefactRatings): boolean {
  const artefacts = run.artefacts ?? [];
  const criteria = run.artefact_review_criteria;
  if (!artefacts.length || !criteria) return true;

  for (const artefact of artefacts) {
    const rating = ratings[artefact.artifact_id];
    if (!rating || rating.human_rag === null || rating.human_approval === null) return false;
    if (!failCommentsComplete(criteria, rating.answers, rating.check_comments)) return false;
    const derived = deriveRag(criteria, rating.answers);
    const overridden = derived.rag !== null && rating.human_rag !== derived.rag;
    const needsNote = rating.human_rag === "amber" || rating.human_rag === "red" || overridden;
    if (needsNote && !rating.human_note.trim()) return false;
  }
  return true;
}
